import { z } from 'zod';
import { AutomationTraceModeSchema } from './trace';
import { WorkspaceScanMetadataSchema } from './source-read';

export const EXECUTION_JOB_STATUSES = ['queued', 'leased', 'running', 'completed', 'failed', 'cancelled'] as const;
export type ExecutionJobStatus = (typeof EXECUTION_JOB_STATUSES)[number];
export const ExecutionJobStatusSchema = z.enum(EXECUTION_JOB_STATUSES);

export const EXECUTION_TARGET_KINDS = ['saas', 'agent', 'cloud', 'explicit'] as const;
export type ExecutionTargetKind = (typeof EXECUTION_TARGET_KINDS)[number];
export const ExecutionTargetKindSchema = z.enum(EXECUTION_TARGET_KINDS);

export const SOURCE_REPO_ACCESS_MODES = ['saas_oauth', 'agent_mediated', 'ci_push'] as const;
export type SourceRepoAccessMode = (typeof SOURCE_REPO_ACCESS_MODES)[number];
export const SourceRepoAccessModeSchema = z.enum(SOURCE_REPO_ACCESS_MODES);

export const ResolvedExecutionTargetSchema = z.object({
  kind: z.enum(['saas', 'agent', 'cloud', 'unavailable']),
  mode: z.enum(['now', 'queue', 'client']),
  reason: z.string().optional(),
  tokenId: z.string().uuid().optional(),
});
export type ResolvedExecutionTarget = z.infer<typeof ResolvedExecutionTargetSchema>;

export const EXECUTION_JOB_KINDS = ['automation', 'repository_access'] as const;
export type ExecutionJobKind = (typeof EXECUTION_JOB_KINDS)[number];
export const ExecutionJobKindSchema = z.enum(EXECUTION_JOB_KINDS);

export const RepositoryAccessJobPayloadSchema = z.object({
  repoId: z.string().uuid().optional(),
  sha: z.string().min(7).max(64).optional(),
  provider: z.enum(['github', 'gitlab', 'bitbucket', 'azure_devops', 'local']).optional(),
  host: z.string().optional(),
  owner: z.string().optional(),
  name: z.string().optional(),
  defaultBranch: z.string().optional(),
  accessMode: z.enum(['agent_mediated', 'workspace_scan', 'path_probe']).default('agent_mediated'),
  mode: z.enum(['git_compare', 'workspace_scan', 'workspace_path_probe']).default('git_compare'),
  workspaceRoot: z.string().min(1).max(500).optional(),
  workspaceId: z.string().uuid().optional(),
});
export type RepositoryAccessJobPayload = z.infer<typeof RepositoryAccessJobPayloadSchema>;

export const RepositoryAccessResultSchema = z.object({
  files: z.array(z.string()),
  skippedPaths: z.array(z.string()),
  ephemeralReads: z.number().int().nonnegative(),
  scan: WorkspaceScanMetadataSchema.optional(),
  /** Present when mode is workspace_path_probe. */
  pathProbe: z
    .object({
      exists: z.boolean(),
      isDirectory: z.boolean(),
      resolvedPath: z.string().optional(),
    })
    .optional(),
});
export type RepositoryAccessResult = z.infer<typeof RepositoryAccessResultSchema>;

export const ExecutionJobPollResponseSchema = z.object({
  job: z
    .object({
      id: z.string().uuid(),
      jobKind: ExecutionJobKindSchema,
      runId: z.string().uuid().optional(),
      projectId: z.string().uuid(),
      flowId: z.string().uuid().optional(),
      leaseNonce: z.string(),
    })
    .nullable(),
});
export type ExecutionJobPollResponse = z.infer<typeof ExecutionJobPollResponseSchema>;

export const ExecutionJobAcceptResponseSchema = z.object({
  jobId: z.string().uuid(),
  jobKind: z.enum(['automation', 'repository_access']).optional(),
  runId: z.string().uuid(),
  projectId: z.string().uuid(),
  flowId: z.string().uuid(),
  leaseNonce: z.string(),
  environment: z.string(),
  browser: z.string(),
  /** v0.46.1 — when `on`, agent should capture and upload a Playwright trace. */
  traceMode: AutomationTraceModeSchema.optional(),
  flow: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    steps: z.array(z.record(z.unknown())),
    settings: z.object({
      baseUrl: z.string(),
      timeout: z.number(),
      retries: z.number(),
      selfHealing: z.boolean(),
      headless: z.boolean().optional(),
    }),
  }),
  testDataContext: z
    .object({
      data: z.record(z.unknown()).optional(),
      environment: z.record(z.string()).optional(),
      datasets: z.record(z.array(z.record(z.string(), z.string()))).optional(),
    })
    .optional(),
  prepare: z
    .object({
      setup: z.array(z.record(z.unknown())).optional(),
      teardown: z.array(z.record(z.unknown())).optional(),
      phaseTags: z
        .object({
          setup: z.array(z.string()),
          teardown: z.array(z.string()),
        })
        .optional(),
      locatorCache: z.array(z.record(z.unknown())).optional(),
    })
    .optional(),
});
export type ExecutionJobAcceptResponse = z.infer<typeof ExecutionJobAcceptResponseSchema>;

export const ExecutionJobRunVaultResponseSchema = z.object({
  secrets: z.record(z.string()),
});
export type ExecutionJobRunVaultResponse = z.infer<typeof ExecutionJobRunVaultResponseSchema>;

export const ExecutionTargetSummarySchema = z.object({
  kind: z.enum(['saas', 'agent', 'cloud']),
  id: z.string(),
  label: z.string(),
  status: z.enum(['healthy', 'unavailable', 'degraded']),
  capabilities: z.record(z.unknown()).optional(),
});
export type ExecutionTargetSummary = z.infer<typeof ExecutionTargetSummarySchema>;

export const ListExecutionTargetsResponseSchema = z.object({
  projectId: z.string().uuid(),
  targets: z.array(ExecutionTargetSummarySchema),
});
export type ListExecutionTargetsResponse = z.infer<typeof ListExecutionTargetsResponseSchema>;
